import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import {
  Calendar,
  Ruler,
  FileText,
  Truck,
  Clock,
  CheckCircle,
  Sparkles,
} from "lucide-react";
import { CTAButton } from "@/components/common/CTAButton";
import { SectionHeader } from "@/components/common/SectionHeader";
import { GlassCard } from "@/components/common/GlassCard";
import { howItWorksImages } from "@/lib/marketing/images";

const steps = [
  {
    number: "01",
    icon: Calendar,
    title: "Schedule a Pickup",
    description: "Book a convenient time slot for our stylist to visit. Choose from morning, afternoon, or evening windows that fit your schedule.",
    details: [
      "Flexible 2-hour pickup windows",
      "No advance payment required",
      "Easy rescheduling if plans change",
    ],
    image: howItWorksImages[0],
  },
  {
    number: "02",
    icon: Ruler,
    title: "Measurement & Collection",
    description: "Our trained stylist arrives at your doorstep with professional tools. They'll take precise measurements and discuss your requirements.",
    details: [
      "Professional measurement session (15-20 mins)",
      "Measurements saved to your profile",
      "Garments tagged and securely packed",
    ],
    image: howItWorksImages[1],
    highlight: "First visit includes a detailed body measurement. Future orders are faster since your measurements are already on file.",
  },
  {
    number: "03",
    icon: FileText,
    title: "Transparent Quote",
    description: "Within hours of pickup, receive a detailed quote with itemized pricing. Review and approve before work begins.",
    details: [
      "Itemized breakdown of all work",
      "No hidden charges, ever",
      "Modify or cancel without fees",
    ],
    highlight: "Work begins only after your approval. Take your time to review.",
  },
  {
    number: "04",
    icon: Sparkles,
    title: "Expert Craftsmanship",
    description: "Your garments are entrusted to experienced tailors who specialize in Indian women's fashion.",
    details: [
      "Skilled professionals with 10+ years experience",
      "Multiple quality checkpoints",
      "Real-time tracking in your dashboard",
    ],
  },
  {
    number: "05",
    icon: Truck,
    title: "Delivery & Delight",
    description: "Your perfectly fitted garments arrive back at your doorstep, cleaned, pressed, and ready to wear.",
    details: [
      "Scheduled delivery at your convenience",
      "Try on garments during delivery",
      "Pay only when satisfied",
    ],
    image: howItWorksImages[2],
    highlight: "Not quite right? We offer complimentary adjustments within 7 days.",
  },
];

export default function HowItWorksPage() {
  return (
    <div className="pt-20">
      <section className="zaklyn-section">
        <div className="zaklyn-container">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-3xl mx-auto text-center mb-16"
          >
            <h1 className="zaklyn-heading-display text-foreground mb-6">
              Your Journey to the Perfect Fit
            </h1>
            <p className="zaklyn-body-lg">
              From booking to delivery, we've crafted a seamless experience that respects your time and exceeds your expectations.
            </p>
          </motion.div>

          <div className="space-y-8 md:space-y-12">
            {steps.map((step, index) => (
              <motion.div
                key={step.number}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-50px" }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <GlassCard hover={false} className="overflow-hidden">
                  <div className={`flex flex-col ${index % 2 === 1 ? "md:flex-row-reverse" : "md:flex-row"}`}>
                    {step.image && (
                      <div className="relative w-full md:w-2/5 aspect-[4/3] md:aspect-auto">
                        <img
                          src={step.image.src}
                          alt={step.image.alt}
                          className="object-cover w-full h-full"
                        />
                      </div>
                    )}
                    <div className={`flex-1 p-6 md:p-10 ${!step.image ? "md:max-w-4xl md:mx-auto" : ""}`}>
                      <div className="flex items-start gap-4 mb-4">
                        <span className="font-display text-5xl font-bold text-primary/20">
                          {step.number}
                        </span>
                        <div className="pt-2">
                          <div className="flex items-center gap-3 mb-2">
                            <step.icon className="h-5 w-5 text-primary" />
                            <h2 className="font-display text-2xl font-semibold text-foreground">
                              {step.title}
                            </h2>
                          </div>
                          <p className="zaklyn-body">{step.description}</p>
                        </div>
                      </div>

                      <ul className="space-y-2 mt-6">
                        {step.details.map((detail, i) => (
                          <li key={i} className="flex items-center gap-3 text-foreground">
                            <CheckCircle className="h-4 w-4 text-primary flex-shrink-0" />
                            <span className="text-sm">{detail}</span>
                          </li>
                        ))}
                      </ul>

                      {step.highlight && (
                        <div className="mt-6 p-4 bg-accent/50 rounded-xl border border-primary/10">
                          <p className="text-sm text-foreground">
                            {step.highlight}
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                </GlassCard>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="zaklyn-section bg-secondary/30">
        <div className="zaklyn-container">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="max-w-3xl mx-auto"
          >
            <GlassCard hover={false} className="p-8 md:p-12">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                  <Clock className="h-6 w-6 text-primary" />
                </div>
                <div>
                  <h3 className="font-display text-2xl font-semibold text-foreground mb-4">
                    Our 24-Hour Promise
                  </h3>
                  <p className="zaklyn-body mb-6">
                    From the moment your garments reach our workshop, the clock starts. Standard alterations are completed within 24 hours, guaranteed.
                  </p>
                  <div className="space-y-3">
                    <div className="flex items-start gap-3">
                      <CheckCircle className="h-4 w-4 text-primary flex-shrink-0 mt-0.5" />
                      <p className="text-sm text-foreground">
                        Orders received before 11 AM are prioritized for same-day completion
                      </p>
                    </div>
                    <div className="flex items-start gap-3">
                      <CheckCircle className="h-4 w-4 text-primary flex-shrink-0 mt-0.5" />
                      <p className="text-sm text-foreground">
                        Express service available for select areas
                      </p>
                    </div>
                    <div className="flex items-start gap-3">
                      <CheckCircle className="h-4 w-4 text-primary flex-shrink-0 mt-0.5" />
                      <p className="text-sm text-foreground">
                        Complex work (heavy embroidery, structural changes) may require 48 hours—we'll inform you upfront
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </GlassCard>
          </motion.div>
        </div>
      </section>

      <section className="zaklyn-section">
        <div className="zaklyn-container">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="max-w-2xl mx-auto text-center"
          >
            <h2 className="zaklyn-heading-2 text-foreground mb-4">
              Ready to Experience the Zaklyn Difference?
            </h2>
            <p className="zaklyn-body mb-8">
              Join thousands of women who trust us with their most cherished garments.
            </p>
            <CTAButton href="/customer/measurements/book" showArrow>
              Book Your First Appointment
            </CTAButton>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
